<template>
    <div class="sticky top-0">
        <div
            id="campaign-basket"
            class="bg-primary-5 border rounded-4 p-16 min-h-128 max-h-564 overflow-y-auto"
        >
            <p class="text-body font-semibold mb-16">{{ $t('general.basket') + ` (${itemCount})` }}</p>
            <div v-for="item in (collapse ? selectedItems : shortList)" class="flex justify-between my-8 group">
                <p class="truncate w-3/5 mr-16">{{ item.name + getAlias(item)}}</p>
                <div class="flex justify-end items-center w-2/5 text-primary space-x-8">
                    <p
                        v-if="!Number.isNaN(item.price.amount) && item.price.amount > 0 && item.price.currency !== 'credits'"
                        class="min-w-fit inline-flex items-center font-semibold"
                    >
                        <currency/>
                        {{ item.price.amount.toFixed(2) }}
                    </p>
                    <p v-else class="min-w-fit font-semibold">{{ $t('multi-posting.price.free') }}</p>
                    <div v-if="form.editable" @click="removeItem(item)" class="w-16 h-16">
                        <SvgIcon v-if="allowDelete" type="cancel" class="w-16 h-16 hover:text-semantic-error cursor-pointer hidden group-hover:block" />
                    </div>
                </div>
            </div>
            <div
                v-if="itemCount > 5 && !collapse"
                class="flex justify-center cursor-pointer underline"
                @click="collapse = true"
            >
                <p>{{ `+${itemCount - 5} ${$t('campaigns.editor.steps.channels')}` }}</p>
            </div>
            <div
                v-if="collapse"
                class="flex justify-center"
                @click="collapse = false"
            >
                <p class="underline cursor-pointer">{{ $t('general.show_less') }}</p>
            </div>
            <div class="border my-16"></div>
            <div class="flex justify-between">
                <p class="font-semibold">{{ $t('general.total') }}</p>
                <p class="font-semibold inline-flex items-center"><currency/>{{ totalPrice }}</p>
            </div>
        </div>
        <div v-if="step === 4 && form.editable">
            <div class="flex justify-center items-center my-16 rounded-4">
                <Checkbox
                    v-if="!currentCompany.in_trial"
                    v-model="termsAndConditions"
                    spanClass="font-normal"
                    checkboxAlignment="top"
                ><span class="select-none" v-html="$t('multi-posting.campaigns.form.purchase_campaign.info')"></span></Checkbox>
                <banner
                    v-else
                    inline
                    warning
                    :description="$t('jobs.no_sharing_in_free_trial')"
                />
            </div>
        </div>
        <div v-if="form.editable" class="flex justify-end flex-wrap mt-16 gap-16">
            <SimpleButton
                v-if="step !== 1 && includeProgressionButton"
                display="secondary"
                class="grow max-w-full"
                icon="keyboard-arrow-left"
                icon-position="left"
                :disabled="!form.editable || purchasing"
                @click="previous"
            >
                {{ $t('general.pagination.previous') }}
            </SimpleButton>
            <SimpleButton
                v-if="step !== 4 && includeProgressionButton"
                display="primary"
                class="grow max-w-full"
                icon="keyboard-arrow-right"
                :disabled="!form.editable"
                :loading="validatingPostingRequirements"
                @click="next"
            >
                {{ $t('multi-posting.campaigns.form.next') }}
            </SimpleButton>
            <SimpleButton
                v-else
                display="primary"
                class="grow max-w-full"
                :disabled="disablePurchaseButton"
                :loading="purchasing"
                @click="purchaseCampaign"
            >
                {{ $t(purchaseButtonLabel) }}
            </SimpleButton>
        </div>
    </div>
</template>

<script>
import SvgIcon from "../../ui/SvgIcon.vue";
import SimpleButton from "../../ui/SimpleButton.vue";
import {mapActions, mapState} from "pinia";
import {useMultiPostingStore} from '../../../state/modules/multiPosting';
import {useAuthenticationStore} from '../../../state/modules/authentication';
import Checkbox from "../../forms/Checkbox.vue";
import Currency from "../../Currency.vue";
import Banner from "../../ui/Banner.vue";

export default {
    name: 'Basket',
    components: {Banner, Checkbox, SimpleButton, SvgIcon, Currency},
    props: {
        step: {
            type: Number,
            required: true,
        },
        includeProgressionButton: {
            type: Boolean,
            default: true,
        },
        purchaseButtonLabel: {
            type: String,
            default: 'multi-posting.campaigns.form.purchase_campaign.button',
        },
        allowDelete: {
            type: Boolean,
            default: true,
        }
    },
    data() {
        return {
            termsAndConditions: false,
            collapse: false,
        }
    },
    computed: {
        ...mapState(useMultiPostingStore, ['form', 'selectedItems', 'purchasing', 'validatingPostingRequirements']),
        ...mapState(useAuthenticationStore, ['currentUser', 'currentCompany']),
        itemCount() {
            return this.selectedItems.length;
        },
        totalPrice() {
            return this.selectedItems.filter(x => x.type !== 'contract')
                .reduce((acc, item) => {
                    return acc + Number(parseFloat(item.price.amount).toFixed(2));
                }, 0).toFixed(2);
        },
        shortList() {
            return this.selectedItems.slice(0, 5);
        },
        disablePurchaseButton() {
            return this.selectedItems.length === 0 || !this.termsAndConditions || !!this.currentCompany.in_trial;
        },
    },
    methods: {
        ...mapActions(useMultiPostingStore, ['removeSelectedItemById', 'setPurchasing']),
        maxDuration() {
            let duration = 0
            for (let i = 0; i < this.selectedItems.length; i++) {
                const item = this.selectedItems[i]
                if (!item || !item.duration) {
                    break;
                }
                const itemDuration = item.duration.period
                if (itemDuration > duration) {
                    duration = itemDuration
                }
            }
            return duration
        },
        currency() {
            return this.selectedItems.length > 0 ? this.selectedItems[0].price.currency : ""
        },
        requestedUser() {
            return {
                "id": this.currentUser.id,
                "name": this.currentUser.name,
            }
        },
        removeItem(item) {
            if (!this.allowDelete) return;
            if (!this.form.editable) return;
            if (item.type === 'contract') {
                this.removeSelectedItemById({itemId: item.contract_id, ref: "contract_id"});
            } else {
                this.removeSelectedItemById({itemId: item.id});
            }

            this.$emit('saveProducts', {p: this.selectedItems, inRemoval: true});
        },
        next() {
            if (!this.form.editable) return;
            if (this.step === 1) {
                this.$emit('saveProducts', {p: this.selectedItems});
            } else if (this.step === 2) {
                this.$emit('saveDetails', this.wrap(this.form));
            } else if (this.step === 3) {
                bus.dispatch('postingRequirementsNext')
            }
        },
        previous() {
            if (!this.form.editable) return;
            if (this.step !== 3) {
                this.$emit('previous');
            } else {
                bus.dispatch('postingRequirementsPrevious')
            }
        },
        purchaseCampaign() {
            if (this.currentCompany.in_trial || !this.form.editable || !this.termsAndConditions) {
                return;
            }
            this.setPurchasing(true);
            this.$emit('purchase');
        },
        wrap(form) {
            const price = this.totalPrice;
            const currency = this.currency();
            const duration = this.maxDuration();
            const user = this.requestedUser();
            const campaign = {
                "price": price,
                "currency": currency,
                "duration": duration,
                "userId": user.id,
                "requestedBy": user.name,
                "vacancyName": form.title
            }
            return {...form.data, ...campaign}
        },
        getAlias(item) {
            return item && item.alias ? ` (${item.alias})` : '';
        },
    },
    watch: {
        itemCount() {
            if (this.itemCount <= 5) {
                this.collapse = false;
            }
        }
    },
}
</script>
